/**
 * SFDR Principal Adverse Impact Dashboard — Full-Stack
 * ════════════════════════════════════════════════════════
 * 
 * Route: /frameworks/sfdr
 * API:   GET /api/v1/sfdr/pais       (live PAI data from graph)
 *        GET /api/v1/sfdr/readiness   (readiness metrics)
 *        GET /api/v1/sfdr/esrs-mapping (ESRS→PAI mapping)
 *        GET /api/v1/sfdr/gaps        (gap analysis)
 *        POST /api/v1/sfdr/pais/:id/status (update PAI status)
 * 
 * Materiality linkage:
 *   When E1 is material in the graph → PAIs 1-6 auto-activate here.
 *   WebSocket listener refetches when materiality.verdict.changed fires.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import {
  useFetch, useMutation, useGraphWS, useModuleNav,
  apiFetch, LoadingState, ErrorState, EmptyState, WSIndicator,
} from "../services/api_service";

const C = {
  bg: "#050a14", surface: "#0c1220", card: "#111c30", cardHover: "#162040",
  border: "#1a2744", borderHi: "#2a3a5c", text: "#e2e8f0", muted: "#6b7a96",
  dim: "#384660", blue: "#3b82f6", amber: "#f59e0b", emerald: "#10b981",
  purple: "#8b5cf6", cyan: "#06b6d4", red: "#ef4444", pink: "#ec4899",
  teal: "#14b8a6", orange: "#f97316", lime: "#84cc16", white: "#fff",
  indigo: "#6366f1", rose: "#f43f5e",
};

const Pl = ({ color: cl, children, small, onClick, active, glow }) => (
  <span onClick={onClick} style={{ display:"inline-flex",alignItems:"center",gap:3,padding:small?"1px 6px":"3px 9px",borderRadius:99,fontSize:small?8:10,fontWeight:600,background:active?cl+"22":cl+"10",color:cl,border:`1px solid ${active?cl+"66":cl+"22"}`,cursor:onClick?"pointer":"default",transition:"all .2s",boxShadow:glow?`0 0 6px ${cl}33`:"none" }}>{children}</span>
);
const Bar = ({ v, mx, cl, h=5 }) => (
  <div style={{ width:"100%",height:h,borderRadius:99,background:C.border,overflow:"hidden" }}>
    <div style={{ width:`${Math.min((v/mx)*100,100)}%`,height:"100%",borderRadius:99,background:`linear-gradient(90deg,${cl}88,${cl})`,transition:"width .4s" }}/>
  </div>
);

const STATUS_CONF = {
  approved:    { label:"Approved",    color:C.emerald, icon:"✓" },
  in_review:   { label:"In Review",   color:C.amber,   icon:"◎" },
  calculated:  { label:"Calculated",  color:C.cyan,    icon:"⚙" },
  not_started: { label:"Not Started", color:C.red,     icon:"○" },
};
const QUALITY_CONF = {
  high:   { label:"High",   color:C.emerald },
  medium: { label:"Medium", color:C.amber },
  low:    { label:"Low",    color:C.red },
};


export default function SFDRPage() {
  const [view, setView] = useState("dashboard");
  const [expandedPAI, setExpandedPAI] = useState(null);
  const [catFilter, setCatFilter] = useState(null);
  const nav = useModuleNav();

  // ── Data fetching (only PAIs on mount; others lazy) ──
  const { data: paiData, loading: paiLoading, error: paiError, refetch: refetchPAIs } = useFetch("/sfdr/pais");

  // Lazy-loaded data (fetched when tab becomes active)
  const [mappingData, setMappingData] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [gapData, setGapData] = useState(null);
  const [gapLoading, setGapLoading] = useState(false);
  const [gapError, setGapError] = useState(null);

  const refetchMapping = useCallback(async () => {
    setMappingData(null);
    setMapError(null);
    setMapLoading(true);
    const result = await apiFetch("/sfdr/esrs-mapping");
    if (result?._error) { setMapError(result); } else { setMappingData(result); }
    setMapLoading(false);
  }, []);

  const refetchGaps = useCallback(async () => {
    setGapData(null);
    setGapError(null);
    setGapLoading(true);
    const result = await apiFetch("/sfdr/gaps");
    if (result?._error) { setGapError(result); } else { setGapData(result); }
    setGapLoading(false);
  }, []);

  const fetchMapping = useCallback(async () => {
    if (mappingData) return;
    setMapLoading(true);
    const result = await apiFetch("/sfdr/esrs-mapping");
    if (result?._error) { setMapError(result); } else { setMappingData(result); }
    setMapLoading(false);
  }, [mappingData]);

  const fetchGaps = useCallback(async () => {
    if (gapData) return;
    setGapLoading(true);
    const result = await apiFetch("/sfdr/gaps");
    if (result?._error) { setGapError(result); } else { setGapData(result); }
    setGapLoading(false);
  }, [gapData]);

  useEffect(() => {
    if (view === "mapping" && !mappingData && !mapLoading) fetchMapping();
    if (view === "gaps" && !gapData && !gapLoading) fetchGaps();
  }, [view, mappingData, mapLoading, gapData, gapLoading, fetchMapping, fetchGaps]);

  // ── WebSocket: auto-refetch on materiality change ──
  const { lastEvent, connected } = useGraphWS();
  useEffect(() => {
    if (lastEvent?.type === "materiality.verdict.changed" || 
        lastEvent?.type === "cascade.completed" ||
        lastEvent?.type === "sfdr.pai.activated") {
      refetchPAIs();
      setMappingData(null);
      setGapData(null);
    }
  }, [lastEvent, refetchPAIs]);

  // ── Derived data ──
  const pais = paiData?.pais || [];
  const graphConnected = paiData?.graph_connected ?? false;
  const mapping = mappingData?.mapping || {};
  const gaps = gapData || {};

  const categories = useMemo(() => [...new Set(pais.map(p => p.category))], [pais]);
  const filteredPAIs = catFilter ? pais.filter(p => p.category === catFilter) : pais;

  const kpis = useMemo(() => {
    const total = pais.length;
    const dataAvailable = pais.filter(p => ["approved","calculated"].includes(p.data_status)).length;
    const inReview = pais.filter(p => p.data_status === "in_review").length;
    const notStarted = pais.filter(p => p.data_status === "not_started").length;
    const material = pais.filter(p => p.materiality_status).length;
    const highQuality = pais.filter(p => p.data_quality === "high").length;
    const pct = total > 0 ? Math.round((dataAvailable / total) * 100) : 0;
    return { total, dataAvailable, inReview, notStarted, material, highQuality, pct };
  }, [pais]);

  // ── Status update mutation ──
  const [updatingPAI, setUpdatingPAI] = useState(null);
  const updatePAIStatus = async (paiId, newStatus) => {
    setUpdatingPAI(paiId);
    await apiFetch(`/sfdr/pais/${paiId}/status`, { method: "POST", body: { status: newStatus } });
    await refetchPAIs();
    setGapData(null);
    setUpdatingPAI(null);
  };

  const views = [
    { id:"dashboard",l:"Readiness Dashboard",i:"📊" },
    { id:"pais",l:"18 PAI Indicators",i:"📋" },
    { id:"mapping",l:"ESRS → PAI Mapping",i:"🔗" },
    { id:"gaps",l:"Gap Analysis",i:"⚠️" },
  ];


  // ═══ DASHBOARD ═══
  const DashboardView = () => {
    if (paiLoading) return <LoadingState message="Calculating SFDR readiness…" />;
    if (paiError) return <ErrorState error={paiError} onRetry={refetchPAIs} />;

    const pct = kpis.pct;

    return (
      <div>
        {/* Readiness Hero */}
        <div style={{ padding:"16px 18px",borderRadius:10,background:`linear-gradient(135deg,${C.indigo}08,${C.cyan}04)`,border:`1px solid ${C.indigo}22`,marginBottom:12,textAlign:"center" }}>
          <div style={{ fontSize:40,fontWeight:800,color:pct>=60?C.emerald:pct>=30?C.amber:C.red }}>{pct}%</div>
          <div style={{ fontSize:12,fontWeight:700,color:C.text }}>SFDR Reporting Readiness</div>
          <div style={{ maxWidth:300,margin:"8px auto 0" }}><Bar v={pct} mx={100} cl={pct>=60?C.emerald:pct>=30?C.amber:C.red} h={6}/></div>
          <div style={{ fontSize:9,color:C.muted,marginTop:6 }}>
            {kpis.dataAvailable}/{kpis.total} indicators ready · {kpis.inReview} in review · {kpis.notStarted} not started
          </div>
          <div style={{ marginTop:6,display:"flex",justifyContent:"center",gap:4 }}>
            <WSIndicator connected={connected} />
            {graphConnected ? <Pl color={C.emerald} small>Neo4j linked</Pl> : <Pl color={C.red} small>Graph offline</Pl>}
            {!graphConnected && <span style={{ fontSize:8,color:C.muted }}>(materiality status may be stale)</span>}
          </div>
        </div>

        {/* KPI Grid */}
        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:6,marginBottom:12 }}>
          {[
            { l:"Total PAI Indicators",v:kpis.total,c:C.text,sub:"mandatory" },
            { l:"Data Available",v:kpis.dataAvailable,c:C.emerald,sub:"approved + calculated" },
            { l:"In Review",v:kpis.inReview,c:C.amber,sub:"pending approval" },
            { l:"Not Started",v:kpis.notStarted,c:C.red,sub:"collection needed" },
            { l:"Material Topics",v:kpis.material,c:C.purple,sub:graphConnected?"live from graph":"static" },
            { l:"High Quality",v:kpis.highQuality,c:C.teal,sub:"confidence level" },
          ].map((k,i) => (
            <div key={i} style={{ padding:"10px 12px",borderRadius:7,background:C.card,border:`1px solid ${k.c}12` }}>
              <div style={{ fontSize:18,fontWeight:800,color:k.c,lineHeight:1 }}>{k.v}</div>
              <div style={{ fontSize:9,fontWeight:600,color:C.text,marginTop:2 }}>{k.l}</div>
              <div style={{ fontSize:7,color:C.dim }}>{k.sub}</div>
            </div>
          ))}
        </div>

        {/* Category Breakdown */}
        <div style={{ padding:"12px 14px",borderRadius:8,background:C.surface,border:`1px solid ${C.border}`,marginBottom:12 }}>
          <div style={{ fontSize:10,fontWeight:700,color:C.indigo,marginBottom:8 }}>READINESS BY CATEGORY</div>
          {(categories.map(cat => {
            const catPAIs = pais.filter(p => p.category === cat);
            const ready = catPAIs.filter(p => ["approved","calculated"].includes(p.data_status)).length;
            return { category: cat, ready, total: catPAIs.length, pct: catPAIs.length > 0 ? Math.round((ready/catPAIs.length)*100) : 0 };
          })).map(cat => (
            <div key={cat.category} style={{ display:"flex",alignItems:"center",gap:8,marginBottom:6 }}>
              <span style={{ fontSize:10,fontWeight:600,color:C.text,minWidth:120 }}>{cat.category}</span>
              <div style={{ flex:1 }}><Bar v={cat.ready} mx={cat.total} cl={cat.pct>=75?C.emerald:cat.pct>=40?C.amber:C.red} h={5}/></div>
              <span style={{ fontSize:9,fontWeight:700,color:cat.pct>=75?C.emerald:cat.pct>=40?C.amber:C.red,minWidth:60,textAlign:"right" }}>
                {cat.ready}/{cat.total} ({cat.pct}%)
              </span>
            </div>
          ))}
        </div>

        {/* Data Status Distribution */}
        <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6 }}>
          {Object.entries(STATUS_CONF).map(([key,conf]) => {
            const count = pais.filter(p => p.data_status === key).length;
            return (
              <div key={key} onClick={() => { setView("pais"); }} style={{ padding:"10px 12px",borderRadius:7,background:C.card,border:`1px solid ${conf.color}22`,textAlign:"center",cursor:"pointer" }}>
                <div style={{ fontSize:20,marginBottom:2 }}>{conf.icon}</div>
                <div style={{ fontSize:18,fontWeight:800,color:conf.color }}>{count}</div>
                <div style={{ fontSize:8,color:C.muted }}>{conf.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };


  // ═══ 18 PAI LIST ═══
  const PAIListView = () => {
    if (paiLoading) return <LoadingState message="Loading PAI indicators from backend…" />;
    if (paiError) return <ErrorState error={paiError} onRetry={refetchPAIs} />;
    if (!pais.length) return <EmptyState icon="📊" title="No PAI data" subtitle="SFDR service may not be running" />;

    return (
      <div>
        <div style={{ display:"flex",gap:3,marginBottom:10,flexWrap:"wrap",alignItems:"center" }}>
          <span style={{ fontSize:8,color:C.dim }}>FILTER:</span>
          <Pl color={C.text} small onClick={() => setCatFilter(null)} active={!catFilter}>All ({pais.length})</Pl>
          {categories.map(cat => (
            <Pl key={cat} color={C.indigo} small onClick={() => setCatFilter(catFilter===cat?null:cat)} active={catFilter===cat}>
              {cat} ({pais.filter(p=>p.category===cat).length})
            </Pl>
          ))}
        </div>

        <div style={{ display:"flex",flexDirection:"column",gap:3 }}>
          {filteredPAIs.map(pai => {
            const isOpen = expandedPAI === pai.pai_number;
            const sc = STATUS_CONF[pai.data_status] || STATUS_CONF.not_started;
            const matSource = pai.materiality_source;

            return (
              <div key={pai.pai_number}>
                <div onClick={() => setExpandedPAI(isOpen?null:pai.pai_number)}
                  style={{ display:"flex",alignItems:"center",gap:6,padding:"8px 12px",borderRadius:isOpen?"7px 7px 0 0":7,background:isOpen?C.indigo+"06":C.card,border:`1px solid ${isOpen?C.indigo+"33":sc.color+"18"}`,borderBottom:isOpen?"none":undefined,cursor:"pointer",transition:"all .15s" }}>
                  <div style={{ width:26,height:26,borderRadius:6,background:sc.color+"15",border:`1.5px solid ${sc.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:sc.color,flexShrink:0 }}>
                    {pai.pai_number}
                  </div>
                  <span style={{ fontSize:13,flexShrink:0 }}>{pai.icon || "📊"}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:10,fontWeight:600,color:C.text }}>{pai.indicator_name || pai.name}</div>
                    <div style={{ fontSize:8,color:C.muted }}>
                      {pai.category} · {pai.metric} · ESRS {pai.esrs_topic}{pai.esrs_code?` (${pai.esrs_code})`:""}
                    </div>
                  </div>
                  {pai.calculated && <Pl color={C.cyan} small>⚙ Calc</Pl>}
                  {pai.sovereign && <Pl color={C.purple} small>🏛️ Sov</Pl>}
                  <Pl color={sc.color} small>{sc.icon} {sc.label}</Pl>
                  <Pl color={pai.materiality_status?C.emerald:C.dim} small>
                    {pai.materiality_status?"✓ Material":"Not Mat"}
                    {matSource==="graph"?" (live)":""}
                  </Pl>
                  {pai.live_value && <span style={{ fontSize:11,fontWeight:700,color:C.cyan,minWidth:60,textAlign:"right" }}>{pai.live_value}</span>}
                  <span style={{ fontSize:9,color:C.dim,transform:isOpen?"rotate(180deg)":"rotate(0)",transition:"transform .2s" }}>▼</span>
                </div>

                {isOpen && (
                  <div style={{ border:`1px solid ${C.indigo}33`,borderTop:"none",borderRadius:"0 0 7px 7px",background:C.surface,padding:"12px 14px" }}>
                    <div style={{ fontSize:9,color:C.text,lineHeight:1.6,marginBottom:8,padding:"6px 10px",borderRadius:5,background:C.card }}>
                      {pai.description || pai.desc || "—"}
                    </div>

                    <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:8 }}>
                      <div style={{ padding:"6px 10px",borderRadius:5,background:C.card }}>
                        <div style={{ fontSize:7,color:C.muted }}>ESRS Topic</div>
                        <div style={{ fontSize:10,fontWeight:600,color:C.purple }}>{pai.esrs_topic}: {pai.esrs_code || "N/A"}</div>
                      </div>
                      <div style={{ padding:"6px 10px",borderRadius:5,background:C.card }}>
                        <div style={{ fontSize:7,color:C.muted }}>Linked Datapoint</div>
                        <div style={{ fontSize:10,fontWeight:600,color:pai.linked_datapoint_id?C.blue:C.dim }}>
                          {pai.linked_datapoint_id || (pai.calculated?"Calculated metric":"None")}
                        </div>
                        {pai.linked_datapoint_id && (
                          <span style={{ fontSize:7,color:C.blue,cursor:"pointer" }} onClick={() => nav.toEvidence(pai.linked_datapoint_id)}>
                            View evidence →
                          </span>
                        )}
                      </div>
                      <div style={{ padding:"6px 10px",borderRadius:5,background:C.card }}>
                        <div style={{ fontSize:7,color:C.muted }}>Data Quality</div>
                        <div style={{ fontSize:10,fontWeight:600,color:pai.data_quality?QUALITY_CONF[pai.data_quality]?.color:C.dim }}>
                          {pai.data_quality ? QUALITY_CONF[pai.data_quality]?.label : "No data"}
                        </div>
                        {pai.data_owner && <div style={{ fontSize:7,color:C.muted }}>Owner: {pai.data_owner}</div>}
                        {pai.data_updated_at && <div style={{ fontSize:7,color:C.dim }}>Updated: {pai.data_updated_at}</div>}
                      </div>
                    </div>

                    {pai.formula && (
                      <div style={{ padding:"6px 10px",borderRadius:5,background:C.cyan+"06",border:`1px solid ${C.cyan}18`,marginBottom:8 }}>
                        <div style={{ fontSize:8,fontWeight:700,color:C.cyan,marginBottom:2 }}>⚙ CALCULATION FORMULA</div>
                        <div style={{ fontSize:9,color:C.text,fontFamily:"monospace" }}>{pai.formula}</div>
                      </div>
                    )}

                    {/* Materiality cascade status */}
                    <div style={{ padding:"6px 10px",borderRadius:5,background:pai.materiality_status?C.emerald+"06":C.red+"06",border:`1px solid ${pai.materiality_status?C.emerald:C.red}18`,marginBottom:8 }}>
                      <div style={{ fontSize:8,fontWeight:700,color:pai.materiality_status?C.emerald:C.red,marginBottom:2 }}>
                        {pai.materiality_status ? "✓ MATERIALITY CASCADE ACTIVE" : "⚠ ESRS TOPIC NOT MATERIAL"}
                        {matSource==="graph" && <span style={{ fontWeight:400,color:C.dim }}> (live from Neo4j)</span>}
                      </div>
                      <div style={{ fontSize:8,color:C.text }}>
                        {pai.materiality_status
                          ? `ESRS ${pai.esrs_topic} is material → this PAI is automatically required. Data collection ${pai.data_status==="approved"?"complete":"in progress"}.`
                          : `ESRS ${pai.esrs_topic} is not material → PAI still mandatory (SFDR RTS), but data may be estimated. Consider primary data collection for audit readiness.`}
                      </div>
                    </div>

                    {/* Status update actions */}
                    <div style={{ display:"flex",gap:4,alignItems:"center" }}>
                      <span style={{ fontSize:8,color:C.muted }}>Change status:</span>
                      {Object.entries(STATUS_CONF).filter(([k]) => k !== pai.data_status).map(([key, conf]) => (
                        <button key={key} onClick={() => updatePAIStatus(pai.pai_number, key)}
                          disabled={updatingPAI === pai.pai_number}
                          style={{ padding:"3px 8px",borderRadius:4,border:`1px solid ${conf.color}33`,background:conf.color+"08",color:conf.color,fontSize:8,fontWeight:600,cursor:"pointer" }}>
                          {updatingPAI===pai.pai_number?"…":conf.icon+" "+conf.label}
                        </button>
                      ))}
                    </div>

                    {/* Navigation */}
                    <div style={{ display:"flex",gap:6,marginTop:8,fontSize:8 }}>
                      <span style={{ color:C.purple,cursor:"pointer" }} onClick={() => nav.toGraphTab("simulator")}>
                        → Simulate materiality change for {pai.esrs_topic}
                      </span>
                      <span style={{ color:C.blue,cursor:"pointer" }} onClick={() => nav.toVoluntaryFW(pai.esrs_topic)}>
                        → View {pai.esrs_topic} framework requirements
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };


  // ═══ ESRS → PAI MAPPING ═══
  const MappingView = () => {
    if (mapLoading) return <LoadingState message="Loading ESRS → PAI mapping…" />;
    if (mapError) return <ErrorState error={mapError} onRetry={refetchMapping} />;

    return (
      <div>
        <div style={{ fontSize:12,fontWeight:700,color:C.purple,marginBottom:4 }}>ESRS → PAI Indicator Mapping</div>
        <div style={{ fontSize:9,color:C.muted,marginBottom:10 }}>
          When an ESRS topic is material, all linked PAIs auto-activate. 
          {graphConnected ? " Status is live from Neo4j graph." : " Graph offline — status may be stale."}
        </div>

        {Object.entries(mapping).map(([esrsTopic, topicData]) => {
          const paiList = topicData.pais || [];
          const mat = topicData.materiality_status;
          const allReady = paiList.every(p => ["approved","calculated"].includes(p.data_status));

          return (
            <div key={esrsTopic} style={{ padding:"10px 14px",borderRadius:8,background:C.card,border:`1px solid ${mat===true?C.emerald:C.dim}22`,marginBottom:6 }}>
              <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:6 }}>
                <div style={{ width:30,height:30,borderRadius:6,background:C.purple+"15",border:`1.5px solid ${C.purple}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:C.purple }}>{esrsTopic}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:11,fontWeight:700,color:C.text }}>ESRS {esrsTopic}</div>
                  <div style={{ fontSize:8,color:C.muted }}>{paiList.length} PAI indicator{paiList.length>1?"s":""}</div>
                </div>
                <Pl color={mat===true?C.emerald:mat===false?C.dim:C.amber} small>
                  {mat===true?"✓ Material":mat===false?"Not Material":"Unknown"}
                  {topicData.materiality_source==="graph"?" (live)":""}
                </Pl>
                {allReady ? <Pl color={C.emerald} small>✓ All Ready</Pl> : 
                  <Pl color={C.amber} small>
                    {paiList.filter(p=>["approved","calculated"].includes(p.data_status)).length}/{paiList.length} Ready
                  </Pl>}
              </div>
              <div style={{ display:"flex",flexDirection:"column",gap:2 }}>
                {paiList.map(p => {
                  const psc = STATUS_CONF[p.data_status] || STATUS_CONF.not_started;
                  return (
                    <div key={p.pai_number} style={{ display:"flex",alignItems:"center",gap:6,padding:"4px 8px",borderRadius:4,background:C.surface,fontSize:9 }}>
                      <span style={{ fontWeight:700,color:C.indigo,minWidth:28 }}>PAI {p.pai_number}</span>
                      <span style={{ color:C.text,flex:1 }}>{p.indicator_name || p.name}</span>
                      <Pl color={psc.color} small>{psc.icon} {psc.label}</Pl>
                      {p.live_value && <span style={{ fontWeight:600,color:C.cyan }}>{p.live_value}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };


  // ═══ GAP ANALYSIS ═══
  const GapView = () => {
    if (gapLoading) return <LoadingState message="Analysing SFDR data gaps…" />;
    if (gapError) return <ErrorState error={gapError} onRetry={refetchGaps} />;

    const critical = gaps.critical || [];
    const nonCritical = gaps.non_critical || [];
    const actions = gaps.recommended_actions || [];

    if (critical.length === 0 && nonCritical.length === 0) {
      return <EmptyState icon="✓" title="No gaps detected" subtitle="All 18 PAI indicators have data available" />;
    }

    const renderGapRow = (p, isCritical) => {
      const sc = STATUS_CONF[p.data_status] || STATUS_CONF.not_started;
      const borderColor = isCritical ? C.red : C.amber;
      return (
        <div key={p.pai_number} style={{ display:"flex",alignItems:"center",gap:6,padding:"8px 12px",borderRadius:6,background:C.card,border:`1px solid ${borderColor}22`,marginBottom:3,fontSize:9 }}>
          <div style={{ width:22,height:22,borderRadius:5,background:borderColor+"15",border:`1.5px solid ${borderColor}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:borderColor }}>{p.pai_number}</div>
          <span style={{ fontSize:12 }}>{p.icon || "📊"}</span>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:600,color:C.text }}>{p.indicator_name || p.name}</div>
            <div style={{ fontSize:7,color:C.muted }}>ESRS {p.esrs_topic} {isCritical?"(material)":""} · {p.category} · {p.metric}</div>
          </div>
          <Pl color={sc.color} small>{sc.label}</Pl>
          <div style={{ fontSize:8,color:borderColor,fontWeight:600,maxWidth:180 }}>
            {p.data_status==="not_started"?"⚠ No data collection started":"◎ In review — needs approval"}
          </div>
        </div>
      );
    };

    return (
      <div>
        <div style={{ fontSize:12,fontWeight:700,color:C.red,marginBottom:4 }}>
          SFDR Data Gaps — {gaps.total_gaps || critical.length+nonCritical.length} Indicators Need Attention
        </div>
        <div style={{ fontSize:9,color:C.muted,marginBottom:10 }}>
          All 18 PAIs are mandatory. Gaps in material ESRS topics are critical — they affect CSRD + SFDR simultaneously.
          {graphConnected ? " Materiality checked live from graph." : ""}
        </div>

        {critical.length > 0 && (
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:10,fontWeight:700,color:C.red,marginBottom:4 }}>🚨 CRITICAL — Material ESRS topic linked ({critical.length})</div>
            {critical.map(p => renderGapRow(p, true))}
          </div>
        )}

        {nonCritical.length > 0 && (
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:10,fontWeight:700,color:C.amber,marginBottom:4 }}>⚠ NON-CRITICAL — ESRS not material, but PAI still mandatory ({nonCritical.length})</div>
            {nonCritical.map(p => renderGapRow(p, false))}
          </div>
        )}

        {/* Recommended Actions */}
        {actions.length > 0 && (
          <div style={{ padding:"10px 14px",borderRadius:8,background:C.indigo+"06",border:`1px solid ${C.indigo}18` }}>
            <div style={{ fontSize:10,fontWeight:700,color:C.indigo,marginBottom:4 }}>📋 RECOMMENDED ACTIONS</div>
            {actions.map((a,i) => (
              <div key={i} style={{ fontSize:9,color:C.text,lineHeight:1.8 }}>
                <span style={{ fontWeight:700,color:a.priority===1?C.red:a.priority===2?C.amber:C.muted }}>{a.priority}. </span>
                {a.action}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };


  // ═══ RENDER ═══
  return (
    <div style={{ background:C.bg,minHeight:"100vh",color:C.text,fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
      <div style={{ padding:"8px 16px 5px",borderBottom:`1px solid ${C.border}`,position:"sticky",top:0,zIndex:10,background:C.bg+"f0",backdropFilter:"blur(12px)" }}>
        <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:5 }}>
          <div style={{ width:24,height:24,borderRadius:5,background:`linear-gradient(135deg,${C.indigo},${C.purple})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:C.white }}>SF</div>
          <span style={{ fontSize:12,fontWeight:700 }}>DisclosureOS</span>
          <span style={{ color:C.dim }}>/</span>
          <span style={{ fontSize:12,fontWeight:700,color:C.purple }}>Frameworks</span>
          <span style={{ color:C.dim }}>/</span>
          <span style={{ fontSize:12,fontWeight:700,color:C.indigo }}>SFDR</span>
          <div style={{ flex:1 }} />
          <WSIndicator connected={connected} />
          <Pl color={C.emerald} small>{kpis.dataAvailable}/{kpis.total} ready</Pl>
          <Pl color={C.amber} small>{kpis.inReview} reviewing</Pl>
          <Pl color={C.red} small>{kpis.notStarted} gaps</Pl>
        </div>
        <div style={{ display:"flex",gap:3 }}>
          {views.map(v => (
            <button key={v.id} onClick={() => setView(v.id)} style={{
              display:"flex",alignItems:"center",gap:3,padding:"4px 9px",borderRadius:5,
              fontSize:9,fontWeight:600,cursor:"pointer",
              border:`1px solid ${view===v.id?C.indigo:C.border}`,
              background:view===v.id?C.indigo+"15":"transparent",
              color:view===v.id?C.indigo:C.muted,
            }}>{v.i} {v.l}</button>
          ))}
        </div>
      </div>

      <div style={{ padding:"12px 16px 30px",maxWidth:1200,margin:"0 auto" }}>
        {view==="dashboard" && <DashboardView />}
        {view==="pais" && <PAIListView />}
        {view==="mapping" && <MappingView />}
        {view==="gaps" && <GapView />}
      </div>
    </div>
  );
}
